import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, EvidenceRow } from "../lib/api";
import { StatusPill } from "../components/StatusPill";

export function EvidenceList() {
  const [rows, setRows] = useState<EvidenceRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [params, setParams] = useSearchParams();
  const [flash, setFlash] = useState<string>(params.get("deleted") === "1" ? "Evidence withdrawn. Your ledger and blockchain proof are preserved." : "");

  useEffect(() => { void load(); }, []);
  useEffect(() => {
    if (!flash) return;
    // Clear the ?deleted=1 marker so refreshing doesn't re-show the toast.
    if (params.get("deleted")) {
      const next = new URLSearchParams(params);
      next.delete("deleted");
      setParams(next, { replace: true });
    }
    const t = setTimeout(() => setFlash(""), 4000);
    return () => clearTimeout(t);
  }, [flash]);

  async function load(next?: string) {
    setLoading(true); setError("");
    try {
      const r = await api.listMine({ cursor: next, limit: 25 });
      setRows(prev => next ? [...prev, ...r.items] : r.items);
      setCursor(r.nextCursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      {flash && (
        <div className="mb-6 text-sm border border-border bg-surface px-4 py-3 rounded-md text-ink-soft">
          {flash}
        </div>
      )}
      <div className="flex items-baseline justify-between mb-8">
        <div>
          <h2 className="mb-1">Your evidence</h2>
          <p className="text-ink-soft text-sm">Sealed items in reverse chronological order.</p>
        </div>
        <Link to="/app/upload" className="btn-primary">Capture</Link>
      </div>

      {error && <div className="text-aigentico-red text-sm border border-aigentico-red bg-white px-4 py-3 rounded-md mb-6">{error}</div>}

      {loading && rows.length === 0 ? (
        <div className="text-ink-faint text-sm py-24 text-center uppercase tracking-widest">Loading</div>
      ) : rows.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="border border-border rounded-lg overflow-hidden bg-white">
          {rows.map((r, i) => (
            <Link
              to={`/app/evidence/${r.evidenceId}`}
              key={r.evidenceId}
              className={`block px-5 py-4 hover:bg-bg-soft transition-colors ${i > 0 ? "border-t border-border" : ""}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  {r.label && (
                    <div className="text-ink font-semibold text-sm truncate mb-1">{r.label}</div>
                  )}
                  <div className={`font-mono truncate ${r.label ? "text-xs text-ink-soft" : "text-sm text-ink"}`}>{r.filename}</div>
                  <div className="text-xs text-ink-faint mt-1">
                    {[
                      formatWhen(r.createdAt),
                      r.mimeType,
                      Number.isFinite(r.size) ? formatBytes(r.size as number) : null,
                    ].filter(Boolean).join(" · ")}
                  </div>
                  {r.manifestHash && (
                    <div className="hash mt-2 truncate">manifest {r.manifestHash}</div>
                  )}
                </div>
                <StatusPill row={r} />
              </div>
            </Link>
          ))}
        </div>
      )}

      {cursor && (
        <div className="mt-6 text-center">
          <button onClick={() => load(cursor)} className="btn-secondary" disabled={loading}>
            {loading ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="border border-border border-dashed rounded-lg p-12 text-center bg-white">
      <div className="text-aigentico-navy font-display text-2xl mb-3">Nothing sealed yet.</div>
      <p className="text-ink-soft text-sm mb-8 max-w-prose mx-auto">
        Capture your first piece of evidence. Once uploaded, it is hashed, chained, and
        anchored to Bitcoin.
      </p>
      <Link to="/app/upload" className="btn-primary">Capture now</Link>
    </div>
  );
}

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return "size unavailable";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}
